import { Fragment, useEffect, useMemo, useState } from 'react';

/**
 * Demo 5: Erfassungs-Flow — Schritte + Wege. Entwurf zur Abstimmung.
 *
 * Arbeitet mit ECHTEN Stammdaten (nur lesend): Strecken, Wäscheklassen samt
 * ihrer tatsächlich zugeordneten Artikelgruppen und Kunden kommen aus dem
 * System. Sackgassen in der Wege-Liste sind damit echte Sackgassen der
 * aktuellen Konfiguration, keine ausgedachten Beispiele.
 *
 * ES WIRD NICHTS GESPEICHERT. Alle Regler wirken nur im Browser, und
 * "Freigeben" ist im Entwurf grundsätzlich gesperrt - Ausprobieren kann
 * nichts durcheinanderbringen.
 *
 * Zwei Grundsätze des Entwurfs:
 * - Die helle Station läuft ständig live mit - kein Probelauf-Knopf.
 * - "Alle Wege" erzählt jede mögliche Erfahrung als Satz; die Freigabe
 *   hängt an dieser Liste. Verzweigungen stehen dort in Worten, nicht
 *   als Grafik. (Der erste Wurf hatte eine Fäden-Grafik dafür - nach
 *   Rückmeldung vom 14.08. entfernt: erklärte sich nicht selbst, und die
 *   festen SVG-Beschriftungen überlagerten sich je nach Fenstergröße.)
 *
 * Eigene Styles und dunkle Gestaltung bewusst eigenständig: Der Entwurf
 * zeigt die Leitstand-Optik, die es im übrigen System noch nicht gibt.
 */

const CSS = `
  .wkdemo{--grund:#0C1116;--flaeche:#141B22;--flaeche-2:#1B242D;--flaeche-3:#222D38;
    --tinte:#E8EEF4;--tinte-2:#9AA9B8;--tinte-3:#6B7B8A;--linie:#212C36;--linie-2:#2E3B47;
    --akzent:#4FC3D4;--akzent-tief:#0E2E36;--gut:#45C68A;--warn:#EFB34A;--warn-tief:#332708;
    --hart:#F26A45;--hart-tief:#33150C;--entwurf:#B98BE8;
    --f-text:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    --f-zahl:ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace;
    background:var(--grund);color:var(--tinte);font-family:var(--f-text);
    font-size:15px;line-height:1.55;min-height:100vh;-webkit-font-smoothing:antialiased}
  .wkdemo *{box-sizing:border-box}
  .wkdemo h1{font-size:23px;font-weight:600;margin:0;letter-spacing:-.01em}
  .wkdemo h2{font-size:14px;font-weight:600;margin:0}
  .wkdemo .marke{font-family:var(--f-zahl);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--tinte-3)}
  .wkdemo .oben{background:#0A0E13;border-bottom:1px solid var(--linie);display:flex;align-items:center;gap:22px;padding:0 20px;height:54px}
  .wkdemo .logo{font-family:var(--f-zahl);font-size:13px;letter-spacing:.06em;font-weight:600}
  .wkdemo .logo b{color:var(--akzent)}
  .wkdemo .krume{font-size:13.5px;color:var(--tinte-2)}
  .wkdemo .krume span{color:var(--tinte-3);margin:0 8px}
  .wkdemo .innen{max-width:1200px;margin:0 auto;padding:24px 20px 80px;display:flex;flex-direction:column;gap:18px}
  .wkdemo .kopfzeile{display:flex;flex-wrap:wrap;gap:14px;align-items:flex-end;justify-content:space-between}
  .wkdemo .karte{background:var(--flaeche);border:1px solid var(--linie);border-radius:7px}
  .wkdemo .karte-kopf{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 18px;border-bottom:1px solid var(--linie)}
  .wkdemo .karte-rumpf{padding:18px}
  .wkdemo .knopf{font:inherit;font-size:13px;padding:7px 14px;border-radius:5px;cursor:pointer;background:var(--flaeche-2);border:1px solid var(--linie-2);color:var(--tinte)}
  .wkdemo .knopf:hover{background:var(--flaeche-3)}
  .wkdemo .knopf.gesperrt{opacity:.45;cursor:not-allowed}
  .wkdemo .knopf:focus-visible,.wkdemo button:focus-visible{outline:2px solid var(--akzent);outline-offset:2px}
  .wkdemo .stand{display:inline-flex;align-items:center;gap:7px;font-family:var(--f-zahl);font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;padding:4px 9px;border-radius:4px;background:rgba(185,139,232,.14);color:var(--entwurf);border:1px solid rgba(185,139,232,.35)}
  .wkdemo .schrittleiste{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;align-items:stretch}
  .wkdemo .schrittkarte{flex:none;width:152px;background:var(--flaeche-2);border:1px solid var(--linie-2);
    border-radius:6px;padding:11px 12px;cursor:pointer;display:flex;flex-direction:column;gap:6px;
    text-align:left;font:inherit;color:var(--tinte)}
  .wkdemo .schrittkarte:hover{border-color:var(--tinte-3)}
  .wkdemo .schrittkarte[aria-pressed="true"]{border-color:var(--akzent);background:var(--akzent-tief);box-shadow:0 0 0 1px var(--akzent)}
  .wkdemo .schrittkarte .nr{font-family:var(--f-zahl);font-size:10px;color:var(--tinte-3);letter-spacing:.1em}
  .wkdemo .schrittkarte .name{font-size:13.5px;font-weight:500}
  .wkdemo .schrittkarte .pf{font-family:var(--f-zahl);font-size:9.5px;letter-spacing:.06em}
  .wkdemo .schrittkarte .sack{font-family:var(--f-zahl);font-size:9.5px;color:var(--hart)}
  .wkdemo .schrittkarte.aus{opacity:.45;border-style:dashed;cursor:default}
  .wkdemo .pfeil{align-self:center;color:var(--tinte-3);flex:none;font-size:15px}
  .wkdemo .wegfuss{font-size:12.5px;color:var(--tinte-3);margin:10px 2px 0}
  .wkdemo .zweispalt{display:grid;grid-template-columns:380px 1fr;gap:18px;align-items:start}
  @media (max-width:940px){.wkdemo .zweispalt{grid-template-columns:1fr}}
  .wkdemo .regelblock{display:flex;flex-direction:column;gap:16px}
  .wkdemo .feld{display:flex;flex-direction:column;gap:7px}
  .wkdemo .stufen{display:flex;gap:4px}
  .wkdemo .stufen button{flex:1;font:inherit;font-size:12.5px;padding:8px 6px;border-radius:5px;cursor:pointer;background:var(--flaeche-2);border:1px solid var(--linie-2);color:var(--tinte-2)}
  .wkdemo .stufen button[aria-pressed="true"]{background:var(--hart-tief);border-color:var(--hart);color:var(--hart);font-weight:600}
  .wkdemo .stufen button[aria-pressed="true"][data-s="weich"]{background:var(--warn-tief);border-color:var(--warn);color:var(--warn)}
  .wkdemo .stufen button[aria-pressed="true"][data-s="aus"]{background:var(--flaeche-3);border-color:var(--linie-2);color:var(--tinte)}
  .wkdemo .chipzeile{display:flex;flex-wrap:wrap;gap:6px}
  .wkdemo .chip{font-family:var(--f-zahl);font-size:11px;padding:4px 9px;border-radius:4px;background:var(--akzent-tief);color:var(--akzent)}
  .wkdemo .satzregel{background:var(--flaeche-2);border:1px solid var(--linie-2);border-radius:6px;padding:12px 13px;font-size:13.5px;line-height:1.7}
  .wkdemo .hinweistext{font-size:12.5px;color:var(--tinte-3)}
  .wkdemo .station{background:#F4F6F7;color:#16211F;border-radius:8px;padding:22px;min-height:340px;display:flex;flex-direction:column;gap:16px}
  .wkdemo .station .punkte{display:flex;gap:7px}
  .wkdemo .station .p{height:5px;flex:1;border-radius:3px;background:#D3DAD8}
  .wkdemo .station .p.an{background:#0D6068}
  .wkdemo .station .p.rot{background:#C0442A}
  .wkdemo .station h3{margin:0;font-size:20px;font-weight:600;color:#16211F}
  .wkdemo .station .frage-hinweis{font-size:13.5px;color:#5A6A67;margin:-6px 0 0}
  .wkdemo .kacheln{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px}
  .wkdemo .kachel{background:#fff;border:1.5px solid #D3DAD8;border-radius:7px;padding:14px 13px;font-size:15px;font-weight:500;min-height:58px;display:flex;align-items:center;cursor:pointer;font-family:var(--f-text);text-align:left;color:#16211F}
  .wkdemo .kachel:hover{border-color:#9FB0AC}
  .wkdemo .kachel[aria-pressed="true"]{border-color:#0D6068;background:#E1EFF0;color:#0D6068}
  .wkdemo .station .fussleiste{margin-top:auto;display:flex;justify-content:space-between;align-items:center;gap:10px}
  .wkdemo .station .zurueck{background:none;border:0;font:inherit;font-size:14px;color:#5A6A67;cursor:pointer;padding:8px}
  .wkdemo .station .weiter{background:#0D6068;color:#fff;border:0;border-radius:6px;padding:11px 22px;font:inherit;font-size:15px;font-weight:600;cursor:pointer}
  .wkdemo .station .weiter:disabled{opacity:.4;cursor:not-allowed}
  .wkdemo .station .ueberspringen{background:none;border:1px solid #C6CFCC;border-radius:6px;color:#5A6A67;font:inherit;font-size:13.5px;padding:9px 14px;cursor:pointer}
  .wkdemo .sackgasse{background:#FBE9E4;border:1.5px solid #C0442A;border-radius:8px;padding:16px;color:#7C2916}
  .wkdemo .sackgasse b{display:block;font-size:16px;margin-bottom:4px}
  .wkdemo .gewichtwert{font-family:var(--f-zahl);font-size:44px;font-weight:600;color:#16211F}
  .wkdemo .pfad{display:grid;grid-template-columns:20px 1fr auto;gap:4px 12px;padding:12px 0;border-bottom:1px solid var(--linie);align-items:baseline}
  .wkdemo .pfad:last-of-type{border-bottom:0}
  .wkdemo .pfad .z{font-family:var(--f-zahl)}
  .wkdemo .pfad.ok .z{color:var(--gut)}
  .wkdemo .pfad.stopp .z{color:var(--hart)}
  .wkdemo .pfad .meta{font-family:var(--f-zahl);font-size:11.5px;color:var(--tinte-3);white-space:nowrap}
  .wkdemo .pfad.stopp{background:var(--hart-tief);margin:0 -18px;padding:12px 18px;border-radius:5px}
  .wkdemo .pfad .tun{grid-column:2;display:flex;gap:8px;margin-top:6px}
  .wkdemo .stift{font:inherit;font-size:12.5px;background:none;border:1px solid var(--linie-2);color:var(--tinte-2);padding:5px 11px;border-radius:5px;cursor:pointer}
  .wkdemo .stift:hover{color:var(--tinte);background:var(--flaeche-2)}
  .wkdemo .notizblock{border-left:2px solid var(--akzent);background:var(--akzent-tief);padding:12px 15px;font-size:13px;border-radius:0 5px 5px 0}
  .wkdemo .notizblock b.kopf{font-family:var(--f-zahl);font-size:10px;letter-spacing:.13em;text-transform:uppercase;display:block;margin-bottom:4px;color:var(--akzent)}
  .wkdemo .seitenfuss{color:var(--tinte-3);font-size:12.5px;border-top:1px solid var(--linie);padding-top:16px}
  @media (prefers-reduced-motion:reduce){.wkdemo *{transition:none!important}}
`;

type HaltKey = 'strecke' | 'waescheklasse' | 'artikelgruppe' | 'kunde' | 'gewicht';

const HALTE: Array<{ key: HaltKey; nr: string; name: string; pflicht: 'PFLICHT' | 'NACHFRAGE' }> = [
  { key: 'strecke', nr: '1', name: 'Strecke', pflicht: 'PFLICHT' },
  { key: 'waescheklasse', nr: '2', name: 'Wäscheklasse', pflicht: 'PFLICHT' },
  { key: 'artikelgruppe', nr: '3', name: 'Artikelgruppe', pflicht: 'NACHFRAGE' },
  { key: 'kunde', nr: '4', name: 'Kunde', pflicht: 'NACHFRAGE' },
  { key: 'gewicht', nr: '5', name: 'Gewicht', pflicht: 'PFLICHT' },
];

const HALT_TITEL: Record<HaltKey, string> = {
  strecke: 'Schritt 1 · Strecke',
  waescheklasse: 'Schritt 2 · Wäscheklasse',
  artikelgruppe: 'Schritt 3 · Artikelgruppe',
  kunde: 'Schritt 4 · Kunde',
  gewicht: 'Schritt 5 · Gewicht',
};

// Echte Stammdaten, nur lesend geladen
interface Strecke { id: string; name: string }
interface Kunde { id: string; name: string }
interface Klasse {
  id: string;
  code: string;
  name: string;
  /** Verlangt diese Klasse den Artikelgruppen-Schritt? (je Klasse konfigurierbar) */
  agPflicht: boolean;
  /** Effektiv wählbare Gruppen. WICHTIG: leere Whitelist heißt "alle erlaubt". */
  gruppen: string[];
  /** Meldung #13: Bei Heimbewohnerwäsche (RESIDENT) ist der Kunde zwingend. */
  kundeZwingend: boolean;
}

interface CaptureConfigAntwort {
  config: {
    requiresLaundryClass: boolean;
    requiresArticleGroup: boolean;
    allowedArticleGroups: Array<{ id: string; code: string; name: string }>;
    allowedLaundryClasses: Array<{
      id: string;
      code: string;
      name: string;
      requiresArticleGroup?: boolean;
      allowedArticleGroups: Array<{ id: string; code: string; name: string }>;
    }>;
  } | null;
}

function StufenWahl({ start, gesperrt }: { start: 'aus' | 'weich' | 'hart'; gesperrt?: boolean }) {
  const [wert, setWert] = useState(start);
  return (
    <div className="stufen">
      {([['aus', 'Überspringen'], ['weich', 'Nachfragen'], ['hart', 'Nicht weiter']] as const).map(([s, label]) => (
        <button key={s} data-s={s} aria-pressed={wert === s} disabled={gesperrt && s !== start}
          onClick={() => !gesperrt && setWert(s)}>{label}</button>
      ))}
    </div>
  );
}

function Punkte({ aktiv, rot }: { aktiv: number; rot?: number }) {
  return (
    <div className="punkte">
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={`p ${i <= aktiv ? 'an' : ''} ${i === rot ? 'rot' : ''}`} />
      ))}
    </div>
  );
}

interface Lauf {
  schritt: number;
  streckeId: string | null;
  klasseId: string | null;
  gruppe: string | null;
  kundeId: string | null;
}

const MAX_KUNDEN_KACHELN = 6;

export default function CaptureFlowBuilderDemo5Page() {
  const [halt, setHalt] = useState<HaltKey>('waescheklasse');
  const [lauf, setLauf] = useState<Lauf>({ schritt: 1, streckeId: null, klasseId: null, gruppe: null, kundeId: null });

  const [strecken, setStrecken] = useState<Strecke[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [klassen, setKlassen] = useState<Klasse[]>([]);
  const [wkPflicht, setWkPflicht] = useState(false);
  const [alleGruppen, setAlleGruppen] = useState<string[]>([]);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  // Stammdaten laden - dieselben rein lesenden Endpunkte wie Demo 3 und 4.
  useEffect(() => {
    (async () => {
      try {
        const [lRes, cRes, agRes] = await Promise.all([
          fetch('/api/data/lines', { credentials: 'include' }),
          fetch('/api/data/customers', { credentials: 'include' }),
          fetch('/api/data/article-groups', { credentials: 'include' }),
        ]);
        if (agRes.ok) {
          const ags = (await agRes.json()) as Array<{ name: string }>;
          setAlleGruppen(ags.map((g) => g.name));
        }
        if (lRes.ok) {
          const lines = (await lRes.json()) as Strecke[];
          setStrecken(lines);
          if (lines.length > 0) setLauf((l) => ({ ...l, streckeId: l.streckeId ?? lines[0].id }));
        }
        if (cRes.ok) setKunden((await cRes.json()) as Kunde[]);
      } catch {
        setLadefehler('Stammdaten konnten nicht geladen werden.');
      } finally {
        setLaedt(false);
      }
    })();
  }, []);

  // Die echte Zuordnung Wäscheklasse -> Artikelgruppen der gewählten Strecke.
  // Daraus ergeben sich die Wege - und echte Sackgassen, falls es welche gibt.
  useEffect(() => {
    if (!lauf.streckeId) return;
    (async () => {
      try {
        const res = await fetch(`/api/data/capture-config?screen=FL&lineId=${encodeURIComponent(lauf.streckeId!)}`,
          { credentials: 'include' });
        if (!res.ok) return;
        const antwort = (await res.json()) as CaptureConfigAntwort;
        const cfg = antwort.config;
        setWkPflicht(!!cfg?.requiresLaundryClass);
        const globalGruppen = (cfg?.allowedArticleGroups ?? []).map((g) => g.name);
        const roh = cfg?.allowedLaundryClasses ?? [];
        setKlassen(roh.map((lc) => {
          const eigene = (lc.allowedArticleGroups ?? []).map((g) => g.name);
          return {
            id: lc.id,
            code: lc.code,
            name: lc.name,
            // Je-Klasse-Pflicht überschreibt das globale Flag (Heimbewohner-Regel)
            agPflicht: lc.requiresArticleGroup ?? cfg?.requiresArticleGroup ?? false,
            // Leere Whitelist = alle erlaubt: erst die eigene Liste, dann die
            // globale, dann sämtliche Gruppen des Systems.
            gruppen: eigene.length > 0 ? eigene : (globalGruppen.length > 0 ? globalGruppen : []),
            kundeZwingend: lc.code === 'RESIDENT',
          };
        }));
      } catch {
        setLadefehler('Erfassungs-Konfiguration konnte nicht geladen werden.');
      }
    })();
  }, [lauf.streckeId]);

  const klasse = klassen.find((k) => k.id === lauf.klasseId) ?? null;
  const kundeZwingend = !!klasse?.kundeZwingend;
  const effGruppen = (k: Klasse) => (k.gruppen.length > 0 ? k.gruppen : alleGruppen);
  // Sackgasse NUR wenn der Schritt verlangt ist UND wirklich nichts wählbar bleibt
  const sackgassen = useMemo(
    () => klassen.filter((k) => k.agPflicht && k.gruppen.length === 0 && alleGruppen.length === 0),
    [klassen, alleGruppen],
  );
  const streckenName = strecken.find((s) => s.id === lauf.streckeId)?.name ?? '—';

  const neustart = () => setLauf((l) => ({ schritt: 1, streckeId: l.streckeId, klasseId: null, gruppe: null, kundeId: null }));
  // Bedingte Schritte wie im echten Wizard: ohne Klassen-Pflicht von 1 direkt zu 4,
  // ohne Artikelgruppen-Pflicht der gewählten Klasse von 2 direkt zu 4.
  const weiter = () => setLauf((l) => {
    let n = l.schritt + 1;
    if (l.schritt === 1 && !wkPflicht) n = 4;
    if (l.schritt === 2 && klasse && !klasse.agPflicht) n = 4;
    return { ...l, schritt: n };
  });
  const zurueck = () => setLauf((l) => {
    let n = Math.max(1, l.schritt - 1);
    if (l.schritt === 4) {
      if (!wkPflicht) n = 1;
      else if (klasse && !klasse.agPflicht) n = 2;
    }
    return { ...l, schritt: n };
  });

  const fussleiste = (weiterOk: boolean, mitUeberspringen = false) => (
    <div className="fussleiste">
      <button className="zurueck" onClick={zurueck}>‹ Zurück</button>
      <span style={{ display: 'flex', gap: 8 }}>
        {mitUeberspringen && <button className="ueberspringen" onClick={weiter}>Überspringen</button>}
        <button className="weiter" disabled={!weiterOk} onClick={weiter}>Weiter</button>
      </span>
    </div>
  );

  let stationInhalt;
  if (laedt) {
    stationInhalt = (<><Punkte aktiv={1} /><h3>Lade Stammdaten …</h3></>);
  } else if (ladefehler) {
    stationInhalt = (<><Punkte aktiv={1} /><h3>Stammdaten fehlen</h3>
      <p className="frage-hinweis">{ladefehler}</p></>);
  } else if (lauf.schritt === 1) {
    stationInhalt = (<>
      <Punkte aktiv={1} />
      <h3>Welche Strecke?</h3>
      <div className="kacheln">
        {strecken.map((s) => (
          <button key={s.id} className="kachel" aria-pressed={s.id === lauf.streckeId}
            onClick={() => setLauf((l) => ({ ...l, streckeId: s.id, klasseId: null, gruppe: null, kundeId: null }))}>
            {s.name}
          </button>
        ))}
      </div>
      {fussleiste(!!lauf.streckeId)}
    </>);
  } else if (lauf.schritt === 2) {
    stationInhalt = (<>
      <Punkte aktiv={2} />
      <h3>Welche Wäscheklasse?</h3>
      <p className="frage-hinweis">Angabe erforderlich — ohne sie geht es nicht weiter.</p>
      <div className="kacheln">
        {klassen.map((k) => (
          <button key={k.id} className="kachel" aria-pressed={k.id === lauf.klasseId}
            onClick={() => setLauf((l) => ({ ...l, klasseId: k.id, gruppe: null, kundeId: null }))}>
            {k.name}
          </button>
        ))}
      </div>
      {klassen.length === 0 && (
        <p className="frage-hinweis">Auf „{streckenName}" wird ohne Wäscheklasse erfasst — dieser
          Schritt erscheint hier nur, weil der Entwurf ihn zeigt; real würde er übersprungen.</p>
      )}
      {fussleiste(!!lauf.klasseId)}
    </>);
  } else if (lauf.schritt === 3) {
    if (klasse && klasse.agPflicht && effGruppen(klasse).length === 0) {
      stationInhalt = (<>
        <Punkte aktiv={3} rot={3} />
        <h3>Artikelgruppe</h3>
        <div className="sackgasse">
          <b>Hier geht es nicht weiter</b>
          {klasse.name} hat auf der Strecke „{streckenName}" keine Artikelgruppe zugeordnet —
          das ist der echte, aktuelle Stand der Konfiguration, kein Beispiel. Die Wege-Liste
          unten führt diese Sackgasse rot auf; freigeben ließe sich so etwas nicht.
        </div>
        <div className="fussleiste">
          <button className="zurueck" onClick={zurueck}>‹ Zurück</button>
          <span />
        </div>
      </>);
    } else {
      stationInhalt = (<>
        <Punkte aktiv={3} />
        <h3>Welche Artikelgruppe?</h3>
        <p className="frage-hinweis">Zur Auswahl stehen die Gruppen der Klasse „{klasse?.name ?? '—'}".</p>
        <div className="kacheln">
          {(klasse ? effGruppen(klasse) : []).map((g) => (
            <button key={g} className="kachel" aria-pressed={g === lauf.gruppe}
              onClick={() => setLauf((l) => ({ ...l, gruppe: g }))}>{g}</button>
          ))}
        </div>
        {fussleiste(!!lauf.gruppe, !lauf.gruppe)}
      </>);
    }
  } else if (lauf.schritt === 4) {
    const kundenKacheln = kunden.slice(0, MAX_KUNDEN_KACHELN);
    stationInhalt = (<>
      <Punkte aktiv={4} />
      <h3>Für welchen Kunden?</h3>
      <p className="frage-hinweis">
        {kundeZwingend
          ? `Bei ${klasse?.name ?? 'dieser Klasse'} ist die Angabe zwingend.`
          : 'Kann übersprungen werden — dann fragt das System nach dem Grund.'}
      </p>
      <div className="kacheln">
        {kundenKacheln.map((k) => (
          <button key={k.id} className="kachel" aria-pressed={k.id === lauf.kundeId}
            onClick={() => setLauf((l) => ({ ...l, kundeId: k.id }))}>{k.name}</button>
        ))}
      </div>
      {kunden.length > MAX_KUNDEN_KACHELN && (
        <p className="frage-hinweis">… und {kunden.length - MAX_KUNDEN_KACHELN} weitere — an der
          Station mit Suche, hier gekürzt.</p>
      )}
      {fussleiste(!!lauf.kundeId || !kundeZwingend, !kundeZwingend && !lauf.kundeId)}
    </>);
  } else if (lauf.schritt === 5) {
    stationInhalt = (<>
      <Punkte aktiv={5} />
      <h3>Gewicht</h3>
      <div className="gewichtwert">12,4 kg</div>
      <p className="frage-hinweis">Kommt von der Waage — hier nur angedeutet.</p>
      <div className="fussleiste">
        <button className="zurueck" onClick={zurueck}>‹ Zurück</button>
        <button className="weiter" onClick={weiter}>Erfassung abschließen</button>
      </div>
    </>);
  } else {
    stationInhalt = (<>
      <Punkte aktiv={5} />
      <h3>Fertig — und nichts wurde gespeichert.</h3>
      <p className="frage-hinweis">
        5 Schritte · {klasse?.name ?? ''}{kundeZwingend ? ' · Kunde war zwingend' : ''}
      </p>
      <div className="fussleiste">
        <span />
        <button className="weiter" onClick={neustart}>Noch einmal</button>
      </div>
    </>);
  }

  return (
    <div className="wkdemo">
      <style>{CSS}</style>

      <header className="oben">
        <span className="logo">wash<b>·</b>ops</span>
        <span className="krume">Einrichten <span>›</span> Erfassung planen <span>›</span> Vorarbeiter</span>
      </header>

      <main className="innen">

        <div className="kopfzeile">
          <div>
            <div className="marke">Entwurf zur Abstimmung · echte Stammdaten, nur lesend</div>
            <h1>Erfassung planen — Vorarbeiter</h1>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="stand">Entwurf · speichert nichts</span>
            <button className="knopf gesperrt" title="Im Entwurf gesperrt — es wird nichts gespeichert.">Einen Stand zurück</button>
            <button className="knopf gesperrt" title="Im Entwurf grundsätzlich gesperrt — Ausprobieren kann nichts durcheinanderbringen.">Freigeben</button>
          </div>
        </div>

        <section className="karte">
          <div className="karte-kopf">
            <h2>Die Schritte</h2>
            <span className="marke">Reihenfolge = Position · Karte anklicken zum Bearbeiten</span>
          </div>
          <div className="karte-rumpf">
            <div className="schrittleiste">
              {HALTE.map((h, i) => (
                <Fragment key={h.key}>
                  <button className="schrittkarte" aria-pressed={halt === h.key} onClick={() => setHalt(h.key)}>
                    <span className="nr">SCHRITT {h.nr}</span>
                    <span className="name">{h.name}</span>
                    <span className="pf" style={{
                      color: h.key === 'kunde'
                        ? (kundeZwingend ? 'var(--hart)' : 'var(--warn)')
                        : h.pflicht === 'PFLICHT' ? 'var(--hart)' : 'var(--warn)',
                    }}>
                      {h.key === 'kunde' ? (kundeZwingend ? 'ZWINGEND' : 'NACHFRAGE') : h.pflicht}
                    </span>
                    {h.key === 'artikelgruppe' && sackgassen.length > 0 && (
                      <span className="sack">✕ {sackgassen.length} Sackgasse{sackgassen.length > 1 ? 'n' : ''} — s. „Alle Wege"</span>
                    )}
                  </button>
                  {i < HALTE.length - 1 && <span className="pfeil" aria-hidden="true">→</span>}
                </Fragment>
              ))}
              <span className="pfeil" aria-hidden="true" style={{ opacity: 0.35 }}>·</span>
              <div className="schrittkarte aus">
                <span className="nr">AUS</span>
                <span className="name">Substrecke</span>
                <span className="pf" style={{ color: 'var(--tinte-3)' }}>wird nicht abgefragt</span>
              </div>
            </div>
            <p className="wegfuss">
              Ein Schritt nach dem anderen, von links nach rechts — mehr steckt nicht dahinter.
              Verschieben ändert die Reihenfolge (im Entwurf nur Anzeige); abhängige Schritte
              lassen sich nicht vor ihre Quelle ziehen. Was je Wäscheklasse passiert, steht
              unten unter „Alle Wege".
            </p>
          </div>
        </section>

        <div className="zweispalt">
          <section className="karte">
            <div className="karte-kopf"><h2>{HALT_TITEL[halt]}</h2></div>
            <div className="karte-rumpf regelblock">

              {halt === 'strecke' && (<>
                <div className="feld">
                  <span className="marke">Wenn nichts angegeben wird</span>
                  <StufenWahl start="hart" gesperrt />
                  <span className="hinweistext">Ohne Strecke gibt es keine Erfassung — diese Pflicht lässt sich nicht senken.</span>
                </div>
                <div className="feld">
                  <span className="marke">Strecken aus dem System</span>
                  <div className="chipzeile">
                    {strecken.map((s) => <span key={s.id} className="chip">{s.name} ✓</span>)}
                  </div>
                </div>
              </>)}

              {halt === 'waescheklasse' && (<>
                <div className="feld">
                  <span className="marke">Wenn nichts angegeben wird</span>
                  <StufenWahl start="hart" />
                  <span className="hinweistext">„Nachfragen" zeigt ein Fenster mit Begründung — gespeichert wird trotzdem.</span>
                </div>
                <div className="feld">
                  <span className="marke">Freigegebene Klassen auf „{streckenName}"</span>
                  <div className="chipzeile">
                    {klassen.map((k) => <span key={k.id} className="chip">{k.name} ✓</span>)}
                  </div>
                  <span className="hinweistext">Echte Konfiguration dieser Strecke. Gesperrte Klassen erscheinen an der Station gar nicht.</span>
                </div>
              </>)}

              {halt === 'artikelgruppe' && (<>
                <div className="feld">
                  <span className="marke">Wenn nichts angegeben wird</span>
                  <StufenWahl start="weich" />
                </div>
                <div className="feld">
                  <span className="marke">Zuordnung je Wäscheklasse (echter Stand)</span>
                  <div className="satzregel">
                    {klassen.map((k) => (
                      <div key={k.id}>
                        <b>{k.name}</b>: {!k.agPflicht
                          ? <span style={{ color: 'var(--tinte-3)' }}>Schritt wird übersprungen</span>
                          : k.gruppen.length > 0
                            ? k.gruppen.join(' · ')
                            : `alle ${alleGruppen.length} Gruppen (keine Einschränkung)`}
                      </div>
                    ))}
                  </div>
                  <span className="hinweistext">Leere Auswahl heißt im System „alle erlaubt" —
                    eine Sackgasse entsteht nur, wenn der Schritt Pflicht ist und wirklich
                    nichts wählbar bleibt.</span>
                </div>
              </>)}

              {halt === 'kunde' && (<>
                <div className="feld">
                  <span className="marke">Wenn nichts angegeben wird</span>
                  <StufenWahl start="weich" />
                </div>
                <div className="feld">
                  <span className="marke">Zusätzliche Bedingung</span>
                  <div className="satzregel">
                    <b>Kunde</b> wird <b style={{ color: 'var(--hart)' }}>zwingend</b>,
                    wenn <b>Wäscheklasse</b> = <b>Heimbewohnerwäsche</b>
                  </div>
                  <span className="hinweistext">Genau eine Bedingung je Schritt, ohne Verschachtelung. (Meldung #13)</span>
                </div>
              </>)}

              {halt === 'gewicht' && (
                <div className="feld">
                  <span className="marke">Wenn nichts angegeben wird</span>
                  <StufenWahl start="hart" gesperrt />
                  <span className="hinweistext">Ohne Gewicht keine Waschleistung — diese Pflicht lässt sich nicht senken.</span>
                </div>
              )}

            </div>
          </section>

          <section className="karte">
            <div className="karte-kopf">
              <h2>Die Station läuft live mit</h2>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className="marke">{lauf.schritt <= 5 ? `Schritt ${Math.min(lauf.schritt, 5)} von 5` : 'abgeschlossen'}</span>
                <button className="stift" onClick={neustart}>Von vorn</button>
              </div>
            </div>
            <div className="karte-rumpf">
              <div className="station">{stationInhalt}</div>
              <p className="hinweistext" style={{ margin: '12px 0 0' }}>
                Kein Nachbau und kein Probelauf-Knopf — die Vorschau ist immer an und immer
                klickbar, mit den echten Stammdaten. Sie bleibt hell, weil die Station hell ist.
              </p>
            </div>
          </section>
        </div>

        <section className="karte">
          <div className="karte-kopf">
            <h2>Alle Wege auf „{streckenName}"</h2>
            <span className="marke">errechnet aus der echten Konfiguration · die Freigabe hängt an dieser Liste</span>
          </div>
          <div className="karte-rumpf">
            {!wkPflicht && !laedt && (
              <div className="pfad ok"><span className="z">✓</span>
                <span className="satz">Auf <b>{streckenName}</b> wird ohne Wäscheklasse erfasst —
                  Strecke, Kunde, Gewicht.</span>
                <span className="meta">3 Schritte</span></div>
            )}
            {wkPflicht && klassen.map((k) => {
              const n = effGruppen(k).length;
              if (!k.agPflicht) {
                return (
                  <div key={k.id} className="pfad ok"><span className="z">✓</span>
                    <span className="satz">Wer <b>{k.name}</b> wäscht, überspringt die
                      Artikelgruppe — Kunde {k.kundeZwingend ? <b>zwingend</b> : 'Pflicht'}.</span>
                    <span className="meta">4 Schritte</span></div>
                );
              }
              if (n === 0) {
                return (
                  <div key={k.id} className="pfad stopp"><span className="z">✕</span>
                    <span className="satz">Wer <b>{k.name}</b> wählt, kommt nicht weiter —
                      Artikelgruppe ist Pflicht, aber nichts ist wählbar.</span>
                    <span className="meta">Sackgasse</span></div>
                );
              }
              return (
                <div key={k.id} className="pfad ok"><span className="z">✓</span>
                  <span className="satz">Wer <b>{k.name}</b> wäscht, wählt die Artikelgruppe
                    aus {n} Option{n > 1 ? 'en' : ''}{k.gruppen.length === 0 ? ' (keine Einschränkung)' : ''} —
                    Kunde {k.kundeZwingend ? <b>zwingend</b> : 'Pflicht'}.</span>
                  <span className="meta">5 Schritte</span></div>
              );
            })}
            <p className="hinweistext" style={{ margin: '14px 0 0' }}>
              {sackgassen.length > 0
                ? 'Freigeben wäre erst möglich, wenn kein Weg in einer Sackgasse endet. Prüfungen sind keine Fehlermeldungen — sie sind Zeilen dieser Liste.'
                : 'Aktuell endet kein Weg in einer Sackgasse — Freigeben wäre möglich. (Im Entwurf bleibt es trotzdem gesperrt.)'}
            </p>
          </div>
        </section>

        <div className="notizblock">
          <b className="kopf">So liest sich diese Seite</b>
          <b>Oben</b> die Schritte in ihrer Reihenfolge — Klick wählt einen aus. <b>Links</b> die
          Regeln des gewählten Schritts, als Sätze. <b>Rechts</b> die Station, wie sie der
          Mitarbeiter sieht — immer live, einfach durchklicken. <b>Unten</b> jede mögliche Route
          als Satz; solange eine davon in einer Sackgasse endet, ließe sich nichts freigeben.
          Strecken, Klassen, Gruppen und Kunden sind <b>echte Daten aus dem System</b> (nur
          gelesen — gespeichert wird nichts).
        </div>

        <div className="notizblock">
          <b className="kopf">Was vom abgestimmten Konzept (11.08.) bleibt</b>
          Entwurf wirkt nie sofort; Freigeben mit Prüfungen; genau ein Rückgängig-Stand; eine flache
          Bedingung je Schritt, keine Verschachtelung; kein Flowchart-Editor.
          Offen zur Abstimmung: Reihenfolge ändern durch Ziehen? WSA als gleiche Seite mit weniger
          Schritten? Reicht die eine Bedingungsform dauerhaft?
        </div>

        <p className="seitenfuss">
          Entwurf 14.08.2026 · liest echte Stammdaten über die vorhandenen Lese-Endpunkte ·
          speichert nichts, Freigeben ist gesperrt · Voraussetzung für die echte Live-Station
          bleibt die Zerlegung der Wizards (Review M12/M13)
        </p>
      </main>
    </div>
  );
}
